import React from 'react';
import OnboardingModal from './OnboardingModal';

interface AddressData {
  buildingName?: string;
  houseNumber: string;
  street: string;
  town: string;
  postcode: string;
}

interface AddressInputModalProps {
  isOpen: boolean;
  onAddressSubmit: (address: string) => void;
  onSkip: () => void;
  onExit?: () => void;
  title: string;
  description: string;
  allowSkip?: boolean;
  initialAddress?: string;
}

/**
 * Backward compatibility wrapper for OnboardingModal
 * Converts the new AddressData format to a simple string address
 */
const AddressInputModal: React.FC<AddressInputModalProps> = ({
  isOpen,
  onAddressSubmit,
  onSkip,
  onExit,
  title,
  description,
  allowSkip = true,
  initialAddress = ''
}) => {
  // Parse initial address string into AddressData format
  const parseInitialAddress = (addressString: string): AddressData => {
    if (!addressString) {
      return { buildingName: '', houseNumber: '', street: '', town: '', postcode: '' };
    }
    
    // Try to parse "Flat 2 Ferndale Court, 123 Main Street, London, SW1A 1AA" format
    const parts = addressString.split(',').map(p => p.trim());
    
    if (parts.length >= 2) {
      const firstPart = parts[0] || '';
      const secondPart = parts[1] || '';
      const town = parts[2] || '';
      const postcode = parts[3] || '';
      
      // Check if first part looks like a building/flat name (contains 'Flat', 'Apartment', etc.)
      const buildingNamePattern = /^(Flat|Apartment|Unit|Suite|Building)\s+/i;
      if (buildingNamePattern.test(firstPart)) {
        // First part is building name, second part might have house number + street
        const streetMatch = secondPart.match(/^(\d+[a-zA-Z]?)\s+(.+)$/);
        if (streetMatch) {
          return {
            buildingName: firstPart,
            houseNumber: streetMatch[1],
            street: streetMatch[2],
            town,
            postcode
          };
        }
        return {
          buildingName: firstPart,
          houseNumber: '',
          street: secondPart,
          town,
          postcode
        };
      }
      
      // Try to extract house number from first part
      const streetMatch = firstPart.match(/^(\d+[a-zA-Z]?)\s+(.+)$/);
      if (streetMatch) {
        return {
          buildingName: '',
          houseNumber: streetMatch[1],
          street: streetMatch[2],
          town: secondPart,
          postcode: town
        };
      }
      
      return {
        buildingName: '',
        houseNumber: '',
        street: firstPart,
        town: secondPart,
        postcode: town
      };
    }
    
    return { buildingName: '', houseNumber: '', street: addressString, town: '', postcode: '' };
  };

  const handleComplete = (data: { address: AddressData }) => {
    // Convert AddressData back to simple string format
    const { buildingName, houseNumber, street, town, postcode } = data.address;
    
    const addressParts = [];
    if (buildingName) addressParts.push(buildingName);
    if (houseNumber && street) {
      addressParts.push(`${houseNumber} ${street}`);
    } else if (street) {
      addressParts.push(street);
    }
    if (town) addressParts.push(town);
    if (postcode) addressParts.push(postcode);
    
    const fullAddress = addressParts.join(', ');
    onAddressSubmit(fullAddress);
  };

  return (
    <OnboardingModal
      isOpen={isOpen}
      onComplete={handleComplete}
      onExit={onExit || onSkip}
      title={title}
      description={description}
      initialData={{
        address: parseInitialAddress(initialAddress)
      }}
    />
  );
};

export default AddressInputModal;
